import { type ReactNode, type SVGProps } from 'react';

/**
 * Tiny built-in icon set used by Menu, Spotlight, Select, Drawer, etc.
 * Independent from `lucide-react` so the core experience works without
 * the optional peer dep.
 */

export interface BuiltinIconProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

function svg(d: ReactNode, props: BuiltinIconProps): ReactNode {
  const { size = 14, ...rest } = props;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...rest}
    >
      {d}
    </svg>
  );
}

export const ChevronDown = (p: BuiltinIconProps) => svg(<polyline points="3,5 7,10 11,5" />, p);
export const ChevronUp = (p: BuiltinIconProps) => svg(<polyline points="3,9 7,4 11,9" />, p);
export const ChevronLeft = (p: BuiltinIconProps) => svg(<polyline points="9,3 4,7 9,11" />, p);
export const ChevronRight = (p: BuiltinIconProps) => svg(<polyline points="5,3 10,7 5,11" />, p);
export const Close = (p: BuiltinIconProps) => svg(<>
  <line x1="2.5" y1="2.5" x2="11.5" y2="11.5" />
  <line x1="11.5" y1="2.5" x2="2.5" y2="11.5" />
</>, p);
export const Check = (p: BuiltinIconProps) => svg(<polyline points="2.5,7.5 5.5,10 11.5,3.5" />, p);
export const Plus = (p: BuiltinIconProps) => svg(<>
  <line x1="7" y1="2.5" x2="7" y2="11.5" />
  <line x1="2.5" y1="7" x2="11.5" y2="7" />
</>, p);
export const Minus = (p: BuiltinIconProps) => svg(<line x1="2.5" y1="7" x2="11.5" y2="7" />, p);
export const Search = (p: BuiltinIconProps) => svg(<>
  <circle cx="6" cy="6" r="3.5" />
  <line x1="9" y1="9" x2="12" y2="12" />
</>, p);
export const Dots = (p: BuiltinIconProps) => svg(<>
  <circle cx="3" cy="7" r="1" fill="currentColor" />
  <circle cx="7" cy="7" r="1" fill="currentColor" />
  <circle cx="11" cy="7" r="1" fill="currentColor" />
</>, p);
export const DotsVertical = (p: BuiltinIconProps) => svg(<>
  <circle cx="7" cy="3" r="1" fill="currentColor" />
  <circle cx="7" cy="7" r="1" fill="currentColor" />
  <circle cx="7" cy="11" r="1" fill="currentColor" />
</>, p);
export const ExternalLink = (p: BuiltinIconProps) => svg(<>
  <path d="M3 3h4M3 3v4M3 3l5 5" />
  <path d="M11 8v3H3v-3" />
</>, p);
export const ArrowRight = (p: BuiltinIconProps) => svg(<>
  <line x1="2.5" y1="7" x2="11.5" y2="7" />
  <polyline points="8,3.5 11.5,7 8,10.5" />
</>, p);
export const ArrowLeft = (p: BuiltinIconProps) => svg(<>
  <line x1="2.5" y1="7" x2="11.5" y2="7" />
  <polyline points="6,3.5 2.5,7 6,10.5" />
</>, p);
export const Calendar = (p: BuiltinIconProps) => svg(<>
  <rect x="2" y="3" width="10" height="9" rx="1.5" />
  <line x1="2" y1="6" x2="12" y2="6" />
  <line x1="5" y1="1.5" x2="5" y2="4" />
  <line x1="9" y1="1.5" x2="9" y2="4" />
</>, p);
export const Clock = (p: BuiltinIconProps) => svg(<>
  <circle cx="7" cy="7" r="5.5" />
  <polyline points="7,4 7,7.5 9.5,9" />
</>, p);
export const Eye = (p: BuiltinIconProps) => svg(<>
  <path d="M1 7s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4z" />
  <circle cx="7" cy="7" r="1.6" />
</>, p);
export const EyeOff = (p: BuiltinIconProps) => svg(<>
  <path d="M1 7s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4z" />
  <circle cx="7" cy="7" r="1.6" />
  <line x1="1" y1="13" x2="13" y2="1" />
</>, p);
export const Trash = (p: BuiltinIconProps) => svg(<>
  <line x1="2" y1="3.5" x2="12" y2="3.5" />
  <path d="M3.5 3.5v8a1 1 0 001 1h5a1 1 0 001-1v-8" />
  <path d="M5 3.5V2.5h4V3.5" />
</>, p);
export const Edit = (p: BuiltinIconProps) => svg(<>
  <path d="M11.5 4l-1.5-1.5L3.5 9l-.5 2 2-.5L11.5 4z" />
</>, p);
export const Settings = (p: BuiltinIconProps) => svg(<>
  <circle cx="7" cy="7" r="2" />
  <path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.8 2.8l1.4 1.4M9.8 9.8l1.4 1.4M2.8 11.2l1.4-1.4M9.8 4.2l1.4-1.4" />
</>, p);
export const Menu = (p: BuiltinIconProps) => svg(<>
  <line x1="2" y1="4" x2="12" y2="4" />
  <line x1="2" y1="7" x2="12" y2="7" />
  <line x1="2" y1="10" x2="12" y2="10" />
</>, p);
export const Upload = (p: BuiltinIconProps) => svg(<>
  <line x1="7" y1="2" x2="7" y2="9" />
  <polyline points="3.5,5.5 7,2 10.5,5.5" />
  <path d="M2 11h10" />
</>, p);
export const Download = (p: BuiltinIconProps) => svg(<>
  <line x1="7" y1="2" x2="7" y2="9" />
  <polyline points="3.5,5.5 7,9 10.5,5.5" />
  <path d="M2 11h10" />
</>, p);
export const Copy = (p: BuiltinIconProps) => svg(<>
  <rect x="3" y="3" width="8" height="9" rx="1" />
  <path d="M5 3V2.5a1.5 1.5 0 011.5-1.5h3A1.5 1.5 0 0111 2.5V3" />
</>, p);
export const Filter = (p: BuiltinIconProps) => svg(<polygon points="2,3 12,3 8.5,7 8.5,11 5.5,11 5.5,7" />, p);
export const Sort = (p: BuiltinIconProps) => svg(<>
  <path d="M2 4l2-2 2 2M4 2v10" />
  <path d="M12 10l-2 2-2-2M10 12V2" />
</>, p);
export const Info = (p: BuiltinIconProps) => svg(<>
  <circle cx="7" cy="7" r="5.5" />
  <line x1="7" y1="6.5" x2="7" y2="10" />
  <circle cx="7" cy="4" r="0.5" fill="currentColor" />
</>, p);
export const Warning = (p: BuiltinIconProps) => svg(<>
  <path d="M7 1.5L13 12.5H1L7 1.5z" />
  <line x1="7" y1="5" x2="7" y2="8.5" />
  <circle cx="7" cy="10.5" r="0.5" fill="currentColor" />
</>, p);
export const Error = (p: BuiltinIconProps) => svg(<>
  <circle cx="7" cy="7" r="5.5" />
  <line x1="7" y1="4" x2="7" y2="7.5" />
  <circle cx="7" cy="10" r="0.5" fill="currentColor" />
</>, p);
export const Success = (p: BuiltinIconProps) => svg(<>
  <circle cx="7" cy="7" r="5.5" />
  <polyline points="4,7 6.5,9.5 10,5" />
</>, p);

export const BUILTIN_ICONS = {
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Close, Check, Plus, Minus, Search, Dots, DotsVertical, ExternalLink,
  ArrowLeft, ArrowRight,
  Calendar, Clock,
  Eye, EyeOff, Trash, Edit, Settings, Menu, Upload, Download, Copy,
  Filter, Sort,
  Info, Warning, Error, Success
} as const;

export type BuiltinIconName = keyof typeof BUILTIN_ICONS;
